import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppShell from '../../components/AppShell';
import AdminTabs from '../../components/AdminTabs';
import FormShell from '../../components/FormShell';
import ErrorBanner from '../../components/ErrorBanner';
import RichTextEditor from '../../components/RichTextEditor';
import PhotoUploader from '../../components/PhotoUploader';
import { supabase } from '../../lib/supabase';
import { sanitizeRichText } from '@viet-parenting/supabase/sanitize';
import { slugifyWithTimestamp } from '@viet-parenting/supabase/slug';
import type {
  Listing,
  ClassFormat,
  AgeGroup,
  Accent,
  SchoolModel,
  TuitionType,
  CurriculumType,
  ContactMethod,
  Status,
  SupportLanguage,
  Country,
} from '@viet-parenting/supabase/types';
import { ClassroomDetail } from '@viet-parenting/content/detail';
import PreviewModal from '../../components/PreviewModal';
import PreviewButton from '../../components/PreviewButton';
import { draftIdentity } from '../../lib/preview';

const FORMATS: ReadonlyArray<{ value: ClassFormat; label: string }> = [
  { value: 'online', label: 'Online' },
  { value: 'offline', label: 'Offline' },
];

const AGES: ReadonlyArray<{ value: AgeGroup; label: string }> = [
  { value: 'kindergarten', label: 'Mầm non (3–5t)' },
  { value: 'primary', label: 'Tiểu học (6–11t)' },
  { value: 'middle', label: 'THCS (12–15t)' },
  { value: 'above_15', label: 'Trên 15 tuổi' },
];

const DIALECTS: ReadonlyArray<{ value: Accent; label: string }> = [
  { value: 'northern', label: 'Giọng Bắc' },
  { value: 'central', label: 'Giọng Trung' },
  { value: 'southern', label: 'Giọng Nam' },
  { value: 'mixed', label: 'Nhiều giọng' },
];

const SCHOOL_MODELS: ReadonlyArray<{ value: SchoolModel; label: string }> = [
  { value: 'individual', label: 'Cá nhân' },
  { value: 'non_profit', label: 'Tổ chức phi lợi nhuận' },
  { value: 'government_or_school', label: 'Do chính phủ hoặc trường chính khóa tổ chức' },
  { value: 'private_language_school', label: 'Trường dạy tiếng tư nhân' },
  { value: 'community_organized', label: 'Cộng đồng tự tổ chức' },
];

const TUITION_TYPES: ReadonlyArray<{ value: TuitionType; label: string }> = [
  { value: 'free', label: 'Miễn phí' },
  { value: 'paid', label: 'Có phí' },
];

const CURRICULUM_TYPES: ReadonlyArray<{ value: CurriculumType; label: string }> = [
  { value: 'vn_government', label: 'Được tập huấn theo chính phủ VN' },
  { value: 'host_country_government', label: 'Được tập huấn theo chính phủ nước sở tại' },
  { value: 'overseas_vietnamese', label: 'Tự biên soạn bởi người Việt hải ngoại' },
  { value: 'vietnamese_teacher', label: 'Tự biên soạn bởi giáo viên Việt Nam' },
];

const CONTACT_METHODS: ReadonlyArray<{ value: ContactMethod; label: string }> = [
  { value: 'all', label: 'Tất cả' },
  { value: 'website', label: 'Website' },
  { value: 'social_media', label: 'Mạng xã hội' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Số điện thoại' },
];

interface FormState {
  name_vi: string;
  name_en: string;
  short_intro_vi: string;
  short_intro_en: string;
  photos: string[];
  venue_country: string;
  venue_city: string;
  venue_address: string;
  website_url: string;
  social_media_url: string;
  contact_email: string;
  contact_phone: string;
  preferred_contacts: ContactMethod[];
  year_started: string;
  school_model: SchoolModel | '';
  tuition_type: TuitionType | '';
  curriculum_type: CurriculumType | '';
  formats: ClassFormat[];
  age_groups: AgeGroup[];
  dialects: Accent[];
  support_languages: string[];
  class_time_vi: string;
  class_time_en: string;
  highlights_vi: string;
  highlights_en: string;
  status: Status;
  featured: boolean;
}

const EMPTY: FormState = {
  name_vi: '',
  name_en: '',
  short_intro_vi: '',
  short_intro_en: '',
  photos: [],
  venue_country: 'global',
  venue_city: '',
  venue_address: '',
  website_url: '',
  social_media_url: '',
  contact_email: '',
  contact_phone: '',
  preferred_contacts: [],
  year_started: '',
  school_model: '',
  tuition_type: '',
  curriculum_type: '',
  formats: [],
  age_groups: [],
  dialects: [],
  support_languages: [],
  class_time_vi: '',
  class_time_en: '',
  highlights_vi: '',
  highlights_en: '',
  status: 'pending',
  featured: false,
};

/**
 * The database shape of the form, shared by the save mutation and the preview
 * so an admin can never see something a save would not write.
 */
function toPayload(state: FormState) {
  return {
    name_vi: state.name_vi.trim(),
    name_en: state.name_en.trim() || null,
    short_intro_vi: state.short_intro_vi ? sanitizeRichText(state.short_intro_vi) : null,
    short_intro_en: state.short_intro_en ? sanitizeRichText(state.short_intro_en) : null,
    photos: state.photos,
    venue_country: state.venue_country.trim(),
    venue_city: state.venue_city.trim(),
    venue_address: state.venue_address.trim() || null,
    website_url: state.website_url.trim() || null,
    social_media_url: state.social_media_url.trim() || null,
    contact_email: state.contact_email.trim() || null,
    contact_phone: state.contact_phone.trim() || null,
    preferred_contacts: state.preferred_contacts,
    year_started: state.year_started ? Number.parseInt(state.year_started, 10) : null,
    school_model: state.school_model || null,
    tuition_type: state.tuition_type || null,
    curriculum_type: state.curriculum_type || null,
    formats: state.formats,
    age_groups: state.age_groups,
    dialects: state.dialects,
    support_languages: state.support_languages,
    class_time_vi: state.class_time_vi.trim() || null,
    class_time_en: state.class_time_en.trim() || null,
    highlights_vi: state.highlights_vi.trim() || null,
    highlights_en: state.highlights_en.trim() || null,
    status: state.status,
    featured: state.featured,
    };
}

function toPreviewRow(state: FormState, id: string | undefined): Listing {
  return {
    ...draftIdentity(state.name_vi),
    ...(id ? { id } : {}),
    ...toPayload(state),
  } as unknown as Listing;
}

interface Props {
  mode: 'new' | 'edit';
}

export default function AdminListingForm({ mode }: Props) {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [state, setState] = useState<FormState>(EMPTY);
  const [previewing, setPreviewing] = useState(false);
  const [loading, setLoading] = useState(mode === 'edit');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [languages, setLanguages] = useState<SupportLanguage[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);

  useEffect(() => {
    let active = true;
    (async () => {
      const [langRes, countryRes] = await Promise.all([
        supabase.from('support_languages').select('*').order('sort'),
        supabase.from('countries').select('*').order('sort'),
      ]);
      if (!active) return;
      setLanguages((langRes.data ?? []) as SupportLanguage[]);
      setCountries((countryRes.data ?? []) as Country[]);
    })();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (mode === 'new' || !id) return;
    let active = true;
    (async () => {
      const { data, error: dbError } = await supabase
        .from('listings')
        .select('*')
        .eq('id', id)
        .maybeSingle();
      if (!active) return;
      if (dbError || !data) {
        setError(dbError?.message ?? 'Không tìm thấy lớp học.');
        setLoading(false);
        return;
      }
      const l = data as Listing;
      setState({
        name_vi: l.name_vi,
        name_en: l.name_en ?? '',
        short_intro_vi: l.short_intro_vi ?? '',
        short_intro_en: l.short_intro_en ?? '',
        photos: l.photos,
        venue_country: l.venue_country,
        venue_city: l.venue_city,
        venue_address: l.venue_address ?? '',
        website_url: l.website_url ?? '',
        social_media_url: l.social_media_url ?? '',
        contact_email: l.contact_email ?? '',
        contact_phone: l.contact_phone ?? '',
        preferred_contacts: l.preferred_contacts,
        year_started: l.year_started?.toString() ?? '',
        school_model: l.school_model ?? '',
        tuition_type: l.tuition_type ?? '',
        curriculum_type: l.curriculum_type ?? '',
        formats: l.formats,
        age_groups: l.age_groups,
        dialects: l.dialects,
        support_languages: l.support_languages,
        class_time_vi: l.class_time_vi ?? '',
        class_time_en: l.class_time_en ?? '',
        highlights_vi: l.highlights_vi ?? '',
        highlights_en: l.highlights_en ?? '',
        status: l.status,
        featured: l.featured ?? false,
      });
      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [mode, id]);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setState((s) => ({ ...s, [key]: value }));

  const toggle = <T extends string>(key: keyof FormState, value: T) => {
    const list = state[key] as T[];
    update(key, (list.includes(value) ? list.filter((x) => x !== value) : [...list, value]) as FormState[typeof key]);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!state.name_vi.trim()) {
      setError('Tên lớp là bắt buộc.');
      return;
    }
    setSaving(true);
    setError(null);

    const payload = toPayload(state);

    const result =
      mode === 'new'
        ? await supabase
            .from('listings')
            .insert({ ...payload, slug: slugifyWithTimestamp(state.name_vi) })
        : await supabase.from('listings').update(payload).eq('id', id!);

    setSaving(false);
    if (result.error) {
      setError(result.error.message);
      return;
    }
    navigate('/admin/listings');
  };

  const handleDelete = async () => {
    if (mode !== 'edit' || !id) return;
    const ok = window.confirm(
      `Xóa lớp học "${state.name_vi}"? Hành động này không thể hoàn tác.`,
    );
    if (!ok) return;
    setDeleting(true);
    setError(null);
    const { error: dbError } = await supabase.from('listings').delete().eq('id', id);
    setDeleting(false);
    if (dbError) {
      setError(dbError.message);
      return;
    }
    navigate('/admin/listings');
  };

  const title = mode === 'new' ? 'Thêm lớp học mới' : 'Chỉnh sửa lớp học';

  if (loading) {
    return (
      <AppShell>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
          <AdminTabs active="listings" />
          <FormShell title={title} maxWidth="max-w-6xl">
            <p className="text-sm text-gray-400">Đang tải…</p>
          </FormShell>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <AdminTabs active="listings" />
        <FormShell title={title} maxWidth="max-w-6xl">
          <ErrorBanner message={error} />

          <form onSubmit={handleSubmit}>
            <div className="grid lg:grid-cols-2 gap-6">
              <div className="space-y-8">
            <Section title="Thông tin cơ bản">
              <div className="grid grid-cols-2 gap-4">
                <Field label="Quốc gia" required>
                  <select
                    required
                    value={state.venue_country}
                    onChange={(e) => update('venue_country', e.target.value)}
                    className={inputClass}
                  >
                    <option value="global">🌐 Toàn cầu (Global)</option>
                    {countries.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.flag} {c.name_vi} ({c.code})
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Thành phố" required>
                  <input
                    type="text"
                    required
                    value={state.venue_city}
                    onChange={(e) => update('venue_city', e.target.value)}
                    placeholder="Berlin"
                    className={inputClass}
                  />
                </Field>
              </div>
              <Field label="Địa chỉ chi tiết">
                <input
                  type="text"
                  value={state.venue_address}
                  onChange={(e) => update('venue_address', e.target.value)}
                  className={inputClass}
                />
              </Field>
              <Field label="Tên" required>
                <input
                  type="text"
                  required
                  value={state.name_vi}
                  onChange={(e) => update('name_vi', e.target.value)}
                  placeholder="Trường Tiếng Việt Berlin"
                  className={inputClass}
                />
              </Field>
              <Field label="Tên tiếng Anh">
                <input
                  type="text"
                  value={state.name_en}
                  onChange={(e) => update('name_en', e.target.value)}
                  placeholder="Berlin Vietnamese School"
                  className={inputClass}
                />
              </Field>
              <Field label="Short Intro (VI)">
                <RichTextEditor
                  value={state.short_intro_vi}
                  onChange={(html) => update('short_intro_vi', html)}
                  placeholder="Giới thiệu ngắn về lớp học..."
                />
              </Field>
              <Field label="Short Intro (EN)">
                <RichTextEditor
                  value={state.short_intro_en}
                  onChange={(html) => update('short_intro_en', html)}
                  placeholder="Short introduction..."
                />
              </Field>
            </Section>

            <Section
              title="Hình ảnh"
              subtitle="Kéo thả để sắp xếp. Ảnh đầu tiên là ảnh đại diện."
            >
              <PhotoUploader
                value={state.photos}
                onChange={(ids) => update('photos', ids)}
                maxFiles={8}
              />
            </Section>
              </div>

              <div className="space-y-8">
            <Section title="Liên hệ">
              <Field label="Website Link">
                <input
                  type="url"
                  value={state.website_url}
                  onChange={(e) => update('website_url', e.target.value)}
                  placeholder="https://example.com"
                  className={inputClass}
                />
              </Field>
              <Field label="Social media">
                <input
                  type="url"
                  value={state.social_media_url}
                  onChange={(e) => update('social_media_url', e.target.value)}
                  placeholder="https://facebook.com/…"
                  className={inputClass}
                />
              </Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Email liên hệ">
                  <input
                    type="email"
                    value={state.contact_email}
                    onChange={(e) => update('contact_email', e.target.value)}
                    placeholder="hello@example.com"
                    className={inputClass}
                  />
                </Field>
                <Field label="Số điện thoại">
                  <input
                    type="tel"
                    value={state.contact_phone}
                    onChange={(e) => update('contact_phone', e.target.value)}
                    placeholder="+49 30 1234 5678"
                    className={inputClass}
                  />
                </Field>
              </div>
              <CheckboxGroup
                label="Cách liên lạc nhanh nhất"
                options={CONTACT_METHODS}
                values={state.preferred_contacts}
                onToggle={(v) => toggle('preferred_contacts', v)}
              />
            </Section>

            <Section title="Mô hình & hình thức">
              <div className="grid grid-cols-2 gap-4">
                <Field label="Năm bắt đầu">
                  <input
                    type="number"
                    min="1900"
                    max="2100"
                    value={state.year_started}
                    onChange={(e) => update('year_started', e.target.value)}
                    placeholder="2014"
                    className={inputClass}
                  />
                </Field>
                <Field label="Mô hình lớp học">
                  <select
                    value={state.school_model}
                    onChange={(e) => update('school_model', e.target.value as SchoolModel | '')}
                    className={inputClass}
                  >
                    <option value="">— Chọn —</option>
                    {SCHOOL_MODELS.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
              <Field label="Học phí">
                <select
                  value={state.tuition_type}
                  onChange={(e) => update('tuition_type', e.target.value as TuitionType | '')}
                  className={inputClass}
                >
                  <option value="">— Chọn —</option>
                  {TUITION_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </Field>
              <CheckboxGroup
                label="Hình thức học"
                options={FORMATS}
                values={state.formats}
                onToggle={(v) => toggle('formats', v)}
              />
            </Section>

            <Section title="Giáo trình">
              <CheckboxGroup
                label="Độ tuổi"
                options={AGES}
                values={state.age_groups}
                onToggle={(v) => toggle('age_groups', v)}
              />
              <CheckboxGroup
                label="Phương ngữ"
                options={DIALECTS}
                values={state.dialects}
                onToggle={(v) => toggle('dialects', v)}
              />
              <Field label="Loại giáo trình">
                <select
                  value={state.curriculum_type}
                  onChange={(e) =>
                    update('curriculum_type', e.target.value as CurriculumType | '')
                  }
                  className={inputClass}
                >
                  <option value="">— Chọn —</option>
                  {CURRICULUM_TYPES.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </Field>
              <CheckboxGroup
                label="Ngôn ngữ hỗ trợ"
                options={languages.map((l) => ({ value: l.code, label: `${l.name_vi} (${l.name_en})` }))}
                values={state.support_languages}
                onToggle={(v) => toggle('support_languages', v)}
              />
            </Section>

            <Section title="Lịch học & học phí">
              <Field label="Giờ học (VI)">
                <textarea
                  value={state.class_time_vi}
                  onChange={(e) => update('class_time_vi', e.target.value)}
                  placeholder="Thứ Bảy 10:00–11:30"
                  rows={2}
                  className={inputClass}
                />
              </Field>
              <Field label="Giờ học (EN)">
                <textarea
                  value={state.class_time_en}
                  onChange={(e) => update('class_time_en', e.target.value)}
                  placeholder="Saturday 10:00–11:30"
                  rows={2}
                  className={inputClass}
                />
              </Field>
              <Field label="Highlight (VI)">
                <textarea
                  value={state.highlights_vi}
                  onChange={(e) => update('highlights_vi', e.target.value)}
                  placeholder="Tết Nguyên Đán, Trung Thu"
                  rows={2}
                  className={inputClass}
                />
              </Field>
              <Field label="Highlight (EN)">
                <textarea
                  value={state.highlights_en}
                  onChange={(e) => update('highlights_en', e.target.value)}
                  placeholder="Lunar New Year, Mid-Autumn"
                  rows={2}
                  className={inputClass}
                />
              </Field>
            </Section>
              </div>
            </div>

            <div className="sticky bottom-0 z-10 -mx-4 sm:-mx-6 mt-8 px-4 sm:px-6 py-4 bg-white/75 backdrop-blur-md border-t border-gray-200 flex items-center gap-3 flex-wrap">
              <button
                type="button"
                onClick={() => navigate('/admin/listings')}
                className="border border-gray-200 text-gray-700 text-sm font-medium px-5 py-2.5 rounded-lg hover:border-red-300 transition-colors"
              >
                Hủy
              </button>
              <PreviewButton onClick={() => setPreviewing(true)} disabled={!state.name_vi.trim()} />
              {mode === 'edit' && (
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={deleting || saving}
                  className="border border-red-200 text-red-700 text-sm font-medium px-5 py-2.5 rounded-lg hover:bg-red-50 hover:border-red-300 disabled:opacity-50 transition-colors"
                >
                  {deleting ? 'Đang xóa…' : 'Xóa'}
                </button>
              )}
              <div className="flex items-center gap-4 ml-auto text-sm flex-wrap">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={state.featured}
                    onChange={(e) => update('featured', e.target.checked)}
                  />
                  <span className="font-medium text-gray-700">★ Featured</span>
                </label>
                <span className="font-medium text-gray-700">Trạng thái:</span>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="status"
                    value="pending"
                    checked={state.status === 'pending'}
                    onChange={() => update('status', 'pending')}
                  />
                  Pending
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="status"
                    value="verified"
                    checked={state.status === 'verified'}
                    onChange={() => update('status', 'verified')}
                  />
                  Active
                </label>
              </div>
              <button
                type="submit"
                disabled={saving}
                className="bg-red-700 text-white text-sm font-medium px-5 py-2.5 rounded-lg hover:bg-red-800 disabled:opacity-50 transition-colors"
              >
                {saving ? 'Đang lưu…' : mode === 'new' ? 'Tạo lớp học' : 'Lưu thay đổi'}
              </button>
            </div>
          </form>
        </FormShell>
      </div>

      {previewing && (
        <PreviewModal onClose={() => setPreviewing(false)}>
          {(ctx) => <ClassroomDetail listing={toPreviewRow(state, id)} ctx={ctx} />}
        </PreviewModal>
      )}
    </AppShell>
  );
}

const inputClass =
  'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500';

interface SectionProps {
  title: string;
  subtitle?: string;
  children: ReactNode;
}
function Section({ title, subtitle, children }: SectionProps) {
  return (
    <section className="brand-card p-6 space-y-5">
      <div>
        <h2 className="font-semibold">{title}</h2>
        {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
      </div>
      {children}
    </section>
  );
}

interface FieldProps {
  label: string;
  required?: boolean;
  children: ReactNode;
}
function Field({ label, required, children }: FieldProps) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label} {required && <span className="text-rose-500">*</span>}
      </label>
      {children}
    </div>
  );
}

interface CheckboxGroupProps<T extends string> {
  label: string;
  options: ReadonlyArray<{ value: T; label: string }>;
  values: T[];
  onToggle: (value: T) => void;
}
function CheckboxGroup<T extends string>({ label, options, values, onToggle }: CheckboxGroupProps<T>) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
      <div className="flex flex-wrap gap-x-4 gap-y-2">
        {options.map((opt) => (
          <label key={opt.value} className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={values.includes(opt.value)}
              onChange={() => onToggle(opt.value)}
            />
            {opt.label}
          </label>
        ))}
      </div>
    </div>
  );
}
